import { useState, useEffect, useRef } from 'react';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
export function useAccountHistorySync(positionCapId) {
    const [progress, setProgress] = useState({
        isActive: false,
        status: 'queued',
        shouldRefresh: false,
        isComplete: false,
    });
    const lastProcessedMessageRef = useRef(null);
    useEffect(() => {
        if (progress.shouldRefresh) {
            const timer = setTimeout(() => {
                setProgress((prev) => (Object.assign(Object.assign({}, prev), { shouldRefresh: false })));
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [progress.shouldRefresh]);
    useEffect(() => {
        if (!positionCapId) {
            setProgress((prev) => {
                if (!prev.isActive && prev.status === 'queued' && !prev.shouldRefresh) {
                    return prev;
                }
                return {
                    isActive: false,
                    status: 'queued',
                    shouldRefresh: false,
                    isComplete: false,
                };
            });
            lastProcessedMessageRef.current = null;
            return;
        }
        const eventPattern = `account_history_sync:${positionCapId}`;
        const handleSyncMessage = (message) => {
            if (!(message === null || message === void 0 ? void 0 : message.data)) {
                return;
            }
            const data = message.data;
            // Create a unique identifier for this message
            const messageId = `${data.status}-${data.sync_complete}-${data.timestamp || Date.now()}`;
            // Skip if we've already processed this exact message
            if (lastProcessedMessageRef.current === messageId) {
                return;
            }
            lastProcessedMessageRef.current = messageId;
            // Only consider sync "active" when status is "in_progress" (actively syncing)
            // We don't show "queued" status as users don't need to know the backend is checking for updates
            const isActivelySyncing = data.status === 'in_progress' || data.status === 'waiting_for_indexing';
            const newProgress = {
                isActive: isActivelySyncing,
                status: data.status,
                message: data.message,
                shouldRefresh: data.should_refresh === true && data.sync_complete === true,
                isComplete: data.sync_complete === true,
            };
            // Only update if values actually changed
            setProgress((prev) => {
                if (prev.isActive === newProgress.isActive &&
                    prev.status === newProgress.status &&
                    prev.message === newProgress.message &&
                    prev.shouldRefresh === newProgress.shouldRefresh &&
                    prev.isComplete === newProgress.isComplete) {
                    return prev;
                }
                return newProgress;
            });
        };
        // Subscribe to sync messages using the pattern-based listener
        const unsubscribe = alphalendWebSocketService.addMessageListener(eventPattern, handleSyncMessage);
        return () => {
            unsubscribe();
        };
    }, [positionCapId]);
    return progress;
}
